<template>
    <h1 class="mb-1.5 mt-14">Payment Details</h1>
    <p class="mb-4 text-ds-primary-500">Verify the information below and click Next Step to pay your deposit.</p>
    <form
        :action="`/early-deposit/${emdId}/verify-client`"
        class="grid gap-5 py-3"
        method="post"
        @change="onFieldChange"
        @input="toggleSubmitBtn"
    >
        <input
            autocomplete="off"
            name="_token"
            type="hidden"
            :value="csrfToken"
        />
        <div>
            <div class="required-field mb-1 text-content-primary">Property Address:</div>
            <input
                class="form-input w-full rounded read-only:bg-ds-primary-100"
                readonly
                type="text"
                :value="address"
            />
        </div>

        <div>
            <div class="required-field mb-1 text-content-primary">Email:</div>
            <input
                v-model="loginForm.email"
                ref="emailInputRef"
                autocomplete="email"
                class="form-input w-full rounded"
                list="client-email-list"
                name="email"
                pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                placeholder="example@email.com"
                required
                type="email"
            />
            <datalist id="client-email-list">
                <option
                    v-for="client in clients"
                    :key="client.email"
                >
                    {{ client.email }}
                </option>
            </datalist>
            <span class="input-error-msg text-ds-red-600"> Please enter a valid email address </span>
        </div>

        <div class="phone-section">
            <div class="required-field mb-1 text-content-primary">Phone:</div>
            <input
                name="phone"
                type="hidden"
                :value="loginForm.phone"
            />
            <DsPhoneInput
                v-model="loginForm.phone"
                id="early-deposit-phone"
                ref="dsPhoneInput"
                :error="phoneErrorMessage"
                list="client-phone-list"
                required
                @validityChanged="isPhoneValid = $event"
            />
            <datalist id="client-phone-list">
                <option
                    v-for="client in clients"
                    :key="client.phone"
                >
                    {{ client.phone }}
                </option>
            </datalist>
        </div>

        <div class="mt-2">
            <label>
                <input
                    v-model="loginForm.tos"
                    class="form-checkbox mr-2 rounded"
                    name="tos"
                    required
                    type="checkbox"
                />
                <span class="required-field text-sm text-ds-primary-500">
                    I agree to
                    <a
                        :href="termsLink"
                        target="_blank"
                        @click.stop
                        >Terms of Service</a
                    >
                    and
                    <a
                        :href="privacyLink"
                        target="_blank"
                        @click.stop
                        >Privacy Policy</a
                    >
                </span>
            </label>
        </div>

        <button
            class="my-10 block w-full rounded bg-ds-primary-900 px-4 py-2 text-center font-medium text-white transition-opacity disabled:opacity-30"
            :disabled="isSubmitDisabled"
            @click="loginAnalytics.ampTrack('emd login - next step clicked')"
        >
            Next Step →
        </button>
    </form>
</template>

<script setup lang="ts">
import DsPhoneInput from '@/components/design-system/form/input/DsPhoneInput/DsPhoneInput.vue';
import { getEmdRefNumberFromPath } from '@/components/Utils/GetEmdRefNumber';
import { csrfToken } from '@/components/Utils/Helper';
import { useLoginAnalytics } from '@/stores/client/login-analytics.store';
import { computed, nextTick, reactive, ref, useTemplateRef } from 'vue';

const emdId = ref(getEmdRefNumberFromPath());
const loginAnalytics = useLoginAnalytics();

loginAnalytics.ampTrack('emd login - open app');

/** legacy from @see LoginFormComponent.vue  */
document.querySelector('[page-loading]')?.remove();

const props = defineProps<{
    termsLink: string;
    privacyLink: string;
    address: string;
    clients: Array<{ email: string; phone: string }>;
}>();

const emailInputRef = useTemplateRef('emailInputRef');
const phoneInputRef = useTemplateRef('dsPhoneInput');
const loginForm = reactive({
    email: '',
    phone: '',
    tos: '',
});
// LIZA: It feels awkward to me that the validity of the phone number is checked in DsPhoneInput, emitted to the parent, and then the parent sends down the error message if needed
//       Maybe worth a follow up ticket to standardize a better approach
const isPhoneValid = ref(false);
const phoneErrorMessage = computed(() =>
    !isPhoneValid.value && loginForm.phone ? 'Please enter a valid phone number' : undefined,
);

if (props.clients.length === 1) {
    loginForm.email = props.clients[0].email;
    loginForm.phone = props.clients[0].phone;
    loginAnalytics.ampTrack('emd login - email populated', { prefilled: true, email: loginForm.email });
    loginAnalytics.ampTrack('emd login - phone populated', { prefilled: true, phone: loginForm.phone });
}

const autofillPhoneFromEmail = (newEmail: HTMLInputElement['value']) => {
    const foundClient = props.clients.find(({ email }) => email === newEmail);

    if (foundClient?.phone) {
        loginForm.phone = foundClient.phone;
        nextTick(() => phoneInputRef.value?.initializePhoneNumber());
        loginAnalytics.ampTrack('emd login - phone populated', { prefilled: false, phone: loginForm.phone }); // LIZA: please see if there's a tidy way to keep this line down with the rest of the analytics in trackFieldChange
        // trackFieldChange(phoneInputRef.value?.phoneInputRef!);
    }
};

const autofillEmailFromPhone = (newPhone: HTMLInputElement['value']) => {
    const foundClient = props.clients.find(({ phone }) => phone === newPhone);

    if (foundClient?.email) {
        loginForm.email = foundClient.email;
        trackFieldChange(emailInputRef.value!);
    }
};

const isSubmitDisabled = ref(true);
const toggleSubmitBtn = (evt: Event) => {
    if (evt.currentTarget instanceof HTMLFormElement) {
        isSubmitDisabled.value = !(evt.currentTarget.checkValidity() && isPhoneValid.value);
    }
};

const onFieldChange = (evt: Event) => {
    const target = evt.target;
    if (!(target instanceof HTMLInputElement) || !target.value) return;
    const { type, value } = target;

    trackFieldChange(target);

    if (type === 'email') {
        autofillPhoneFromEmail(value);
    } else if (type === 'tel') {
        autofillEmailFromPhone(value);
    }
};

const trackFieldChange = (target: HTMLInputElement) => {
    const { type, value } = target;

    if (type === 'email' && target.checkValidity()) {
        loginAnalytics.ampTrack('emd login - email populated', { prefilled: false, email: value });
    } else if (type === 'tel' && isPhoneValid.value) {
        loginAnalytics.ampTrack('emd login - phone populated', { prefilled: false, phone: loginForm.phone });
    } else if (type === 'checkbox' && target.checked) {
        loginAnalytics.ampTrack('emd login - tos and pp accepted');
    }
};
</script>

<style scoped lang="scss">
.required-field::after {
    content: '*';
    color: red;
}

.input-error-msg {
    display: none;
}

input[type='text'],
input[type='tel'],
input[type='email'] {
    &:not(:placeholder-shown, :focus) {
        &.invalid,
        &:invalid {
            @apply border-2;
            @apply border-ds-red-600;

            ~ .input-error-msg {
                display: initial;
            }
        }
    }
}
</style>
